import { Box, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { centerItemFlex, mediumFont } from "../../../../utils/styles";

const PLANET_ORDER = [
  "sun",
  "moon",
  "mars",
  "mercury",
  "jupiter",
  "venus",
  "saturn",
];

const PLANET_COLORS: Record<string, string> = {
  sun: "#F5A623",
  moon: "#B0BEC5",
  mars: "#E53935",
  mercury: "#66BB6A",
  jupiter: "#8D6E63",
  venus: "#EC407A",
  saturn: "#5C6BC0",
};

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function planetObjectToChartData(planetData: Record<string, number>) {
  if (!planetData || typeof planetData !== "object") return [];
  return PLANET_ORDER.filter((key) => planetData[key] != null).map((key) => ({
    name: capitalize(key),
    planetKey: key,
    value: Number(planetData[key]),
  }));
}

interface CustomProps {
  data: Record<string, number>;
  title: string;
}

const ShadbalaChartComponent = (props: CustomProps) => {
  const chartData = planetObjectToChartData(props.data).sort(
    (a, b) => b.value - a.value,
  );
  if (chartData.length === 0) return null;

  return (
    <Box
      sx={{
        ...centerItemFlex,
        flexDirection: "column",
        width: "100%",
        minWidth: 280,
      }}
    >
      <Typography
        sx={{
          color: "#fff",
          fontSize: 18,
          mb: 1,
          textAlign: "center",
          ...mediumFont,
        }}
      >
        {props.title}
      </Typography>
      <ResponsiveContainer width="100%" height={220} minHeight={200}>
        <BarChart
          data={chartData}
          margin={{ top: 24, right: 16, left: 8, bottom: 4 }}
        >
          <XAxis
            dataKey="name"
            tick={{ fill: "#fff", fontSize: 14 }}
            axisLine={{ stroke: "rgba(255,255,255,0.3)" }}
            tickLine={{ stroke: "rgba(255,255,255,0.2)" }}
          />
          <YAxis
            dataKey="value"
            tick={{ fill: "#fff", fontSize: 14 }}
            axisLine={{ stroke: "rgba(255,255,255,0.3)" }}
            tickLine={{ stroke: "rgba(255,255,255,0.2)" }}
            allowDecimals
            tickFormatter={(v) =>
              Number.isInteger(v) ? String(v) : v.toFixed(1)
            }
          />
          <Tooltip
            cursor={{ fill: "rgba(255,255,255,0.06)" }}
            contentStyle={{
              background: "rgba(30,30,40,0.95)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 8,
            }}
            labelStyle={{ color: "#fff" }}
            itemStyle={{ color: "#fff" }}
            formatter={(value: number) => [value.toFixed(2), ""]}
            labelFormatter={(label) => `${label}`}
          />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={48}>
            <LabelList
              dataKey="value"
              position="top"
              fill="#fff"
              style={{ fontSize: 14, fontWeight: 600 }}
              formatter={(v: number) =>
                Number.isInteger(v) ? v : v.toFixed(1)
              }
            />
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={PLANET_COLORS[entry.planetKey] ?? "#888"}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default ShadbalaChartComponent;
